import React, { useContext } from "react";
import { Link } from "react-router-dom";
import { LanguageContext } from "@/App";
import { MapPin, Phone, Mail } from "lucide-react";

const Footer = () => {
  const { language } = useContext(LanguageContext);

  const content = {
    en: {
      about: "About",
      aboutText:
        "Sri Siddhachal Sthulabhadra Jain Teerth Dham is a sacred spiritual haven dedicated to Jain devotees.",
      quickLinks: "Quick Links",
      contact: "Contact Info",
      address: "Chandra Hills, Karnataka, India",
      phone: "+91 98868 76335",
      email: "info@srisiddhachal.org",
      copyright:
        "© 2025 Sri Siddhachal Sthulabhadra Dham. All rights reserved.",
      taxDisclaimer: "No 80G/tax exemption will be provided for donors.",
    },
    hi: {
      about: "हमारे बारे में",
      aboutText:
        "श्री सिद्धाचल स्थूलभद्र जैन तीर्थ धाम जैन भक्तों को समर्पित एक पवित्र आध्यात्मिक स्थल है।",
      quickLinks: "त्वरित लिंक",
      contact: "संपर्क जानकारी",
      address: "चंद्र हिल्स, कर्नाटक, भारत",
      phone: "+91 98868 76335",
      email: "info@srisiddhachal.org",
      copyright: "© 2025 श्री सिद्धाचल स्थूलभद्र धाम। सर्वाधिकार सुरक्षित।",
      taxDisclaimer: "दाताओं के लिए कोई 80G/कर छूट प्रदान नहीं की जाएगी।",
    },
  };

  const links = {
    en: [
      { name: "Home", path: "/" },
      { name: "Divine Attractions", path: "/divine-attractions" },
      { name: "Gallery", path: "/gallery" },
      { name: "Events", path: "/events" },
      { name: "Donate", path: "/donate" },
      { name: "Contact", path: "/contact" },
    ],
    hi: [
      { name: "होम", path: "/" },
      { name: "दिव्य आकर्षण", path: "/divine-attractions" },
      { name: "गैलरी", path: "/gallery" },
      { name: "कार्यक्रम", path: "/events" },
      { name: "दान करें", path: "/donate" },
      { name: "संपर्क करें", path: "/contact" },
    ],
  };

  const t = content[language];

  return (
    <footer className="bg-gradient-to-br from-amber-900 to-amber-950 text-amber-50 mt-16">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* About Section */}
          <div>
            <h3
              className="text-xl font-bold mb-4"
              data-testid="footer-about-title"
            >
              {t.about}
            </h3>
            <p className="text-amber-100 text-sm leading-relaxed">
              {t.aboutText}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3
              className="text-xl font-bold mb-4"
              data-testid="footer-links-title"
            >
              {t.quickLinks}
            </h3>
            <ul className="space-y-2">
              {links[language].map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    data-testid={`footer-link-${link.path}`}
                    className="text-amber-100 hover:text-white transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3
              className="text-xl font-bold mb-4"
              data-testid="footer-contact-title"
            >
              {t.contact}
            </h3>
            <div className="space-y-3">
              <div className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-amber-400 flex-shrink-0 mt-1" />
                <span className="text-amber-100 text-sm">{t.address}</span>
              </div>
              <div className="flex items-center space-x-3">
                <Phone className="w-5 h-5 text-amber-400 flex-shrink-0" />
                <span className="text-amber-100 text-sm">{t.phone}</span>
              </div>
              <div className="flex items-center space-x-3">
                <Mail className="w-5 h-5 text-amber-400 flex-shrink-0" />
                <span className="text-amber-100 text-sm">{t.email}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-amber-800 mt-8 pt-6 text-center">
          <p className="text-amber-200 text-sm" data-testid="footer-copyright">
            {t.copyright}
          </p>
          <p className="text-amber-400 text-xs mt-2 italic">
            {t.taxDisclaimer}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
